<template>
  <teleport to="body">
    <div v-show="isOpen" class="search-popover" role="dialog">
      <div class="search-bar-container">
        <form
          class="search-bar"
          action="https://www.google.com/search"
          method="GET"
          target="_blank"
          @submit="handleSubmit"
        >
          <input
            ref="inputRef"
            v-model="searchQuery"
            type="text"
            name="q"
            class="search-input"
            placeholder="Google で検索する"
            autocomplete="off"
            @input="debouncedUpdateSuggestions"
            @keydown="handleKeydown"
            @blur="handleBlur"
          />
          <div v-if="isOpen" class="search-suggestions">
            <div v-if="suggestions.length > 0" class="search-suggestions-list">
              <div
                v-for="(suggestion, index) in suggestions"
                :key="`${suggestion.query}-${index}`"
                :class="[
                  'search-suggestion-item',
                  { selected: selectedIndex === index },
                ]"
                @click="selectSuggestion(suggestion)"
                @mouseenter="selectedIndex = index"
              >
                <i
                  :class="[
                    'fas',
                    suggestion.isPredefined ? 'fa-globe' : 'fa-history',
                  ]"
                ></i>
                <div class="suggestion-content">
                  <div class="suggestion-label">{{ suggestion.query }}</div>
                  <div v-if="!suggestion.isPredefined" class="suggestion-meta">
                    {{ suggestion.count }}回 -
                    {{ formatLastAccess(suggestion.lastAccess) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import {
  getSuggestionsFromHistory,
  formatLastAccess,
  recordSearchQuery,
} from "@/utils/storage";
import type { SearchHistoryEntry, SearchSuggestion } from "@/types/models";

const searchQuery = ref("");
const isOpen = ref(false);
const selectedIndex = ref(-1);
const debounceTimer = ref<number | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const clearOnNextOpen = ref(false);

const SEARCH_SUGGESTIONS: SearchSuggestion[] = [
  { label: "GitHub", url: "https://github.com" },
  { label: "Notion", url: "https://www.notion.so" },
  { label: "ChatGPT", url: "https://chat.openai.com" },
  { label: "YouTube", url: "https://www.youtube.com" },
  { label: "Reddit", url: "https://www.reddit.com" },
  { label: "Stack Overflow", url: "https://stackoverflow.com" },
  { label: "MDN", url: "https://developer.mozilla.org" },
  { label: "Wikipedia", url: "https://ja.wikipedia.org" },
  { label: "Amazon", url: "https://www.amazon.com" },
  { label: "Twitter", url: "https://twitter.com" },
];

const suggestions = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return [];

  const lowerQuery = query.toLowerCase();

  // 検索履歴からマッチする候補を取得
  const historyMatches = getSuggestionsFromHistory(query);

  // 定義済みサジェストからマッチする候補を取得
  const predefinedMatches = SEARCH_SUGGESTIONS.filter((suggestion) =>
    suggestion.label.toLowerCase().includes(lowerQuery),
  ).map(
    (suggestion): SearchHistoryEntry => ({
      query: suggestion.label,
      url: suggestion.url,
      count: 0,
      lastAccess: 0,
      firstAccess: 0,
      isPredefined: true,
    }),
  );

  // 履歴と定義済みを合併（履歴が優先）
  const allSuggestions = [...historyMatches, ...predefinedMatches];

  // 重複を削除（queryベース）
  const seen = new Set<string>();
  const uniqueSuggestions = allSuggestions.filter((item) => {
    if (seen.has(item.query.toLowerCase())) {
      return false;
    }
    seen.add(item.query.toLowerCase());
    return true;
  });

  // 表示する候補の最大数
  return uniqueSuggestions.slice(0, 4);
});

const debouncedUpdateSuggestions = (): void => {
  selectedIndex.value = -1;
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }
  debounceTimer.value = window.setTimeout(() => {
    // 候補は computed で自動的に更新される
  }, 300);
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    selectedIndex.value = Math.min(
      selectedIndex.value + 1,
      suggestions.value.length - 1,
    );
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
  } else if (event.key === "Enter") {
    if (selectedIndex.value >= 0 && suggestions.value[selectedIndex.value]) {
      event.preventDefault();
      selectSuggestion(suggestions.value[selectedIndex.value]);
    }
  } else if (event.key === "Escape") {
    isOpen.value = false;
  }
};

const selectSuggestion = (suggestion: SearchHistoryEntry): void => {
  searchQuery.value = suggestion.query;
  recordSearchQuery(suggestion.query, suggestion.url);
  clearOnNextOpen.value = true;
  isOpen.value = false;
};

const handleSubmit = (): void => {
  if (searchQuery.value.trim()) {
    recordSearchQuery(searchQuery.value);
    window.setTimeout(() => {
      searchQuery.value = "";
    }, 0);
  }
  isOpen.value = false;
};

// フォーカス失去時の処理
const handleBlur = (): void => {
  setTimeout(() => {
    isOpen.value = false;
  }, 200);
};

const closePopover = (): void => {
  isOpen.value = false;
};

const openFromShortcut = (clear = false): void => {
  focusSearchInput(clear);
};

const focusSearchInput = (clear = false): void => {
  isOpen.value = true;
  if (clear || clearOnNextOpen.value) {
    searchQuery.value = "";
    clearOnNextOpen.value = false;
  }

  const focusInput = (): void => {
    if (inputRef.value) {
      inputRef.value.focus();
      inputRef.value.select();
    }
  };

  nextTick(() => {
    focusInput();
    window.setTimeout(focusInput, 10);
    window.setTimeout(focusInput, 50);
  });
};

const handleDocumentClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  if (target && !target.closest(".search-bar-container")) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener("click", handleDocumentClick);
});

watch(
  () => isOpen.value,
  (open) => {
    document.body.classList.toggle("search-pop-active", open);
  },
  { immediate: true },
);

defineExpose({
  focusSearchInput,
  openFromShortcut,
  closePopover,
  isOpen,
});
</script>

<style scoped>
.search-bar-container {
  width: 100%;
  position: relative;
}

.search-popover {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16vh 16px 0;
  background: rgba(0, 0, 0, 0.12);
  z-index: 300;
}

.search-bar {
  display: flex;
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  background: white;
  outline: none;
  transition: all 0.2s;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
}

.search-input:focus {
  border-color: #1f73e6;
  box-shadow: 0 1px 6px rgba(31, 115, 230, 0.3);
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.search-suggestions-list {
  display: flex;
  flex-direction: column;
}

.search-suggestion-item {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.search-suggestion-item:last-child {
  border-bottom: none;
}

.search-suggestion-item:hover,
.search-suggestion-item.selected {
  background: #f5f5f5;
}

.search-suggestion-item i {
  color: #5f6368;
  flex: 0 0 auto;
}

.suggestion-content {
  flex: 1;
  min-width: 0;
}

.suggestion-label {
  font-size: 14px;
  color: #202124;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.suggestion-meta {
  font-size: 11px;
  color: #9aa0a6;
  margin-top: 2px;
}
</style>
